import React, { useMemo, useState } from 'react';
import { Alert, Box, Chip, Grid, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import RecordsDialog from '../analytics/owner/RecordsDialog';
import { ChartCard, DrillLink, Section, StatTile } from '../analytics/owner/parts';
import { AXIS, GRID, INK, NEUTRAL, SERIES } from '../analytics/chartTheme';
import { formatClock, formatPct, share, singaporeToday, type DrillSpec, type ExecTile } from '../analytics/owner/ownerData';
import { whenSgt, type FailureKind } from './opsHealth';
import { dayLabel, HISTORY_RANGES, historyChartRows, historyDrill, historyRange, periodText, replyShares, type HistoryDays, type HistoryMetric, type OpsHistory } from './opsHistory';
import { FirstLoad, RangeToggle, StaleNotice } from './trackingParts';
import { useRpc } from './useRpc';

const nf = (n: number | null | undefined) => (n == null ? '-' : n.toLocaleString('en-SG'));
// The failure kinds as counts in a sentence ("8 could not be read as an RFQ").
const FAILURE_COUNT_WORDS: Record<FailureKind, string> = {
  rfq_parse_failed: 'could not be read as an RFQ',
  looks_like_rfq_no_items: 'read like an RFQ but gave no items',
  excel_job_partial: 'Excel fills partly failed',
  excel_job_stuck: 'Excel fills stuck',
};
const FADED = 0.35;

const Grid4: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 2, mb: 2 }}>{children}</Box>
);

/** Newest day first, each number opening the records behind it. */
function DayByDay({
  rows,
  daily,
  drillFor,
  onDrill,
}: {
  rows: ReturnType<typeof historyChartRows>;
  daily: OpsHistory['daily'];
  drillFor: (metric: HistoryMetric, a?: string, b?: string) => DrillSpec;
  onDrill: (spec: DrillSpec) => void;
}) {
  const cell = (metric: HistoryMetric, n: number, day: string, faded?: boolean) =>
    n === 0 ? (
      <Box component="span" sx={{ color: INK.muted }}>
        0
      </Box>
    ) : (
      <Box component="span" sx={{ opacity: faded ? 0.6 : 1 }}>
        <DrillLink spec={drillFor(metric, day, day)} onDrill={onDrill} align="right">
          {nf(n)}
        </DrillLink>
      </Box>
    );

  return (
    <Section id="history-days" title="Day by day" subtitle="Newest first. Select a number to see the emails or RFQs behind it.">
      <Paper variant="outlined">
        <TableContainer sx={{ maxHeight: 560 }}>
          <Table size="small" stickyHeader aria-label="Day by day figures">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Day</TableCell>
                {[
                  'Emails read',
                  'Could not read',
                  'Deleted by a person',
                  'Archived automatically',
                  'RFQs received (mailbox)',
                  'From the system',
                  'By hand',
                  'No email reply',
                  'Median first reply',
                  'Within 4\u00a0h',
                ].map((h) => (
                  <TableCell key={h} align="right" sx={{ fontWeight: 600, minWidth: 84 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {[...rows].reverse().map((r) => {
                const d = daily.find((x) => x.day === r.day)!;
                return (
                  <TableRow key={r.day} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {dayLabel(r.day, true)}
                      {r.partial && <Chip size="small" label="not complete" variant="outlined" sx={{ ml: 1, height: 20, fontSize: 11 }} />}
                    </TableCell>
                    <TableCell align="right">{cell('read', d.rfq_emails_read, r.day)}</TableCell>
                    <TableCell align="right">{cell('could_not_read', d.could_not_read.total, r.day)}</TableCell>
                    <TableCell align="right">{cell('deleted_in_app', d.deleted_in_app, r.day)}</TableCell>
                    <TableCell align="right">{cell('auto_archived', d.auto_archived, r.day)}</TableCell>
                    <TableCell align="right">{cell('threads', d.threads_received, r.day, r.partial)}</TableCell>
                    <TableCell align="right">{cell('replied_system', d.replied_system, r.day, r.partial)}</TableCell>
                    <TableCell align="right">{cell('replied_manual', d.replied_manual, r.day, r.partial)}</TableCell>
                    <TableCell align="right">{cell('no_email_reply', d.no_email_reply, r.day, r.partial)}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', opacity: r.partial ? 0.6 : 1 }}>
                      {d.answered_office ? (
                        <DrillLink spec={drillFor('answered_office', r.day, r.day)} onDrill={onDrill} align="right">
                          {formatClock(d.median_clock_minutes)}
                        </DrillLink>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', opacity: r.partial ? 0.6 : 1 }}>
                      {d.answered_office ? `${d.within_4h} of ${d.answered_office}` : '-'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Section>
  );
}

const HistoryTab: React.FC = () => {
  const [days, setDays] = useState<HistoryDays>(30);
  const [drill, setDrill] = useState<DrillSpec | null>(null);
  const today = singaporeToday();
  const range = useMemo(() => historyRange(days, today), [days, today]);
  const { data, error, loading, reload } = useRpc<OpsHistory>('get_ops_history', { p_from: range.from, p_to: range.to });
  const rows = useMemo(() => (data ? historyChartRows(data) : []), [data]);

  const header = (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 2 }}>
      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720 }}>
        Day by day: mail the system read or could not read, RFQs deleted, replies sent and how fast. Every number opens the emails or RFQs it counts.
      </Typography>
      <RangeToggle label="history" value={days} options={HISTORY_RANGES} onChange={setDays} />
    </Stack>
  );

  if (!data) {
    return (
      <Box>
        {header}
        <FirstLoad error={error} what="the history" onRetry={reload} />
      </Box>
    );
  }

  const { from, to } = data.range;
  const t = data.totals;
  const drillFor = (metric: HistoryMetric, a = from, b = to) => historyDrill(metric, a, b);
  const span = periodText(from, to);
  const partial = rows.filter((r) => r.partial);
  const shares = replyShares(t);
  const stale = loading && (data.range.from !== range.from || data.range.to !== range.to);

  const mailTiles: ExecTile[] = [
    {
      id: 'read',
      label: 'Emails read as an RFQ',
      value: nf(t.rfq_emails_read),
      detail: `${span}. Each became an RFQ in the system, including ones deleted since.`,
      drill: drillFor('read'),
    },
    {
      id: 'failed',
      label: 'Could not be read',
      value: nf(t.could_not_read.total),
      detail: (Object.keys(FAILURE_COUNT_WORDS) as FailureKind[])
        .filter((k) => t.could_not_read[k] > 0)
        .map((k) => `${nf(t.could_not_read[k])} ${FAILURE_COUNT_WORDS[k]}`)
        .join(' · ') || 'Nothing failed.',
      drill: drillFor('could_not_read'),
    },
    {
      id: 'deleted',
      label: 'RFQs deleted by a person',
      value: nf(t.deleted_in_app),
      detail: 'Deleted in the app by a member of staff, counted on the day they were deleted.',
      drill: drillFor('deleted_in_app'),
    },
    {
      id: 'archived',
      label: 'RFQs archived automatically',
      value: nf(t.auto_archived),
      detail: 'Removed by the nightly 03:00 job because they were still unquoted after 3 working days.',
      drill: drillFor('auto_archived'),
    },
  ];

  const replyTiles: ExecTile[] = [
    {
      id: 'threads',
      label: 'RFQs received in the mailbox',
      value: nf(t.threads_received),
      detail: `RFQ email threads that arrived ${span} - the owner's volume measure.`,
      drill: drillFor('threads'),
    },
    {
      id: 'system',
      label: 'Answered from the system',
      value: nf(t.replied_system),
      detail: `${formatPct(shares.viaSystem)} of the ${nf(t.replied_system + t.replied_manual)} answered by email.`,
      drill: drillFor('replied_system'),
    },
    {
      id: 'manual',
      label: 'Answered by hand',
      value: nf(t.replied_manual),
      detail: 'First reply sent from Outlook rather than the system.',
      drill: drillFor('replied_manual'),
    },
    {
      id: 'none',
      label: 'No email reply',
      value: nf(t.no_email_reply),
      detail: `${formatPct(share(t.no_email_reply, t.threads_received))} of RFQs received. Many were answered in a customer portal (Wrist, Seven Seas, ARC) or by phone.`,
      drill: drillFor('no_email_reply'),
    },
  ];

  const speedTiles: ExecTile[] = [
    {
      id: 'median',
      label: 'Median time to first reply',
      value: formatClock(t.median_clock_minutes),
      detail: `Clock time, over ${nf(t.answered_office)} answered RFQs that arrived Mon-Fri 09:00-18:00 (of ${nf(t.office_rfqs)} that arrived then).`,
      drill: drillFor('answered_office'),
    },
    {
      id: '1h',
      label: 'Answered within 1 hour',
      value: formatPct(t.pct_within_1h),
      detail: `${nf(t.within_1h)} of ${nf(t.answered_office)} answered office-hours RFQs.`,
      drill: drillFor('answered_office'),
    },
    {
      id: '4h',
      label: 'Answered within 4 hours',
      value: formatPct(t.pct_within_4h),
      detail: `${nf(t.within_4h)} of ${nf(t.answered_office)} answered office-hours RFQs.`,
      drill: drillFor('answered_office'),
    },
  ];

  const fadedTick = (props: { x: number; y: number; payload: { value: string; index: number } }) => {
    const row = rows[props.payload.index];
    return (
      <text x={props.x} y={props.y + 12} textAnchor="middle" fontSize={AXIS.fontSize} fill={row?.partial ? INK.muted : AXIS.fill}>
        {props.payload.value}
      </text>
    );
  };

  return (
    <Box>
      {header}
      <StaleNotice error={error} onRetry={reload} />
      {stale && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} aria-live="polite">
          Loading {days} days…
        </Typography>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        Mailbox figures (RFQs received, replies, turnaround) come from a scan of the sales mailbox each night at about 03:30; the last scan ran{' '}
        {whenSgt(data.mailbox_scanned_through, 'never')}.{' '}
        {partial.length > 0
          ? `${partial.length === 1 ? `${dayLabel(partial[0].day)} is` : `${dayLabel(partial[0].day)} onwards are`} not complete yet, so those days are faded and left out of the speed chart. Emails read and RFQs deleted are live.`
          : 'Every day shown is complete.'}
      </Alert>

      <Section
        id="history-mail"
        title="Mail in"
        subtitle={`What the system did with RFQ email, ${span} (Singapore days). Emails read, RFQs received under Replies out and RFQs deleted by a person count different things, so they do not add up: emails read is every email the system turned into an RFQ, on the day it read it (including ones deleted later); RFQs received is the RFQ threads the nightly scan finds in the sales mailbox, on the day they arrived - fewer than the system reads; deleted is RFQs removed that day, whenever they arrived.`}
      >
        <Grid4>
          {mailTiles.map((tile) => (
            <StatTile key={tile.id} tile={tile} onDrill={setDrill} />
          ))}
        </Grid4>
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <ChartCard title="Emails read, and those that could not be read" subtitle="By the day the system processed them." height={240}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={GRID} />
                  <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={{ stroke: GRID }} minTickGap={16} />
                  <YAxis tick={AXIS} tickLine={false} axisLine={false} allowDecimals={false} width={36} />
                  <Tooltip cursor={{ fill: 'rgba(42,120,214,0.08)' }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="read" name="Read as an RFQ" stackId="mail" fill={SERIES.aqua} maxBarSize={18} />
                  <Bar dataKey="couldNotRead" name="Could not be read" stackId="mail" fill={INK.bad} radius={[3, 3, 0, 0]} maxBarSize={18} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={5}>
            <ChartCard title="RFQs deleted" subtitle="By the day they were deleted." height={240}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={GRID} />
                  <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={{ stroke: GRID }} minTickGap={16} />
                  <YAxis tick={AXIS} tickLine={false} axisLine={false} allowDecimals={false} width={36} />
                  <Tooltip cursor={{ fill: 'rgba(42,120,214,0.08)' }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="deletedInApp" name="By a person" stackId="del" fill={SERIES.violet} maxBarSize={18} />
                  <Bar dataKey="autoArchived" name="Archived automatically" stackId="del" fill={NEUTRAL} radius={[3, 3, 0, 0]} maxBarSize={18} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        </Grid>
      </Section>

      <Section id="history-replies" title="Replies out" subtitle={`RFQs that arrived in the sales mailbox ${span}, by how DBR first replied.`}>
        <Grid4>
          {replyTiles.map((tile) => (
            <StatTile key={tile.id} tile={tile} onDrill={setDrill} />
          ))}
        </Grid4>
        <ChartCard title="RFQs received, by how they were answered" subtitle="By the day the RFQ arrived. Faded days are not fully scanned yet." height={260}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke={GRID} />
              <XAxis dataKey="label" tick={fadedTick} tickLine={false} axisLine={{ stroke: GRID }} minTickGap={16} />
              <YAxis tick={AXIS} tickLine={false} axisLine={false} allowDecimals={false} width={36} />
              <Tooltip cursor={{ fill: 'rgba(42,120,214,0.08)' }} labelFormatter={(l, p) => `${l}${p?.[0]?.payload?.partial ? ' (not complete yet)' : ''}`} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {(
                [
                  ['system', 'From the system', SERIES.system],
                  ['manual', 'By hand', SERIES.manual],
                  ['noReply', 'No email reply', NEUTRAL],
                ] as const
              ).map(([key, name, fill], i, all) => (
                <Bar key={key} dataKey={key} name={name} stackId="replies" fill={fill} maxBarSize={18} radius={i === all.length - 1 ? [3, 3, 0, 0] : undefined}>
                  {rows.map((r) => (
                    <Cell key={r.day} fillOpacity={r.partial ? FADED : 1} />
                  ))}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </Section>

      <Section
        id="history-speed"
        title="Speed"
        subtitle="Clock time from an RFQ arriving to DBR's first email reply, for RFQs that arrived Mon-Fri 09:00-18:00 Singapore time. Overnight and weekend arrivals are not in these figures."
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 2 }}>
          {speedTiles.map((tile) => (
            <StatTile key={tile.id} tile={tile} onDrill={setDrill} />
          ))}
        </Box>
        <ChartCard
          title="Answered within 1 hour and 4 hours, by day"
          subtitle="Days with fewer than 3 answered office-hours RFQs, and days not fully scanned, are left out. The tooltip gives the count and the median."
          height={240}
          empty={rows.some((r) => r.within4h != null && !r.partial) ? null : 'Not enough answered office-hours RFQs in this period.'}
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows.map((r) => (r.partial ? { ...r, within1h: null, within4h: null } : r))} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke={GRID} />
              <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={{ stroke: GRID }} minTickGap={16} />
              <YAxis tick={AXIS} tickLine={false} axisLine={false} width={44} domain={[0, 100]} tickFormatter={(v: number) => `${v}%`} />
              <Tooltip
                formatter={(value, name, item) => [value == null ? '-' : `${value}% of ${item.payload.answered} answered (median ${formatClock(item.payload.median)})`, name]}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="linear" dataKey="within4h" name="Within 4 h" stroke={SERIES.violet} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 6 }} connectNulls={false} />
              <Line type="linear" dataKey="within1h" name="Within 1 h" stroke={SERIES.aqua} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 6 }} connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </Section>

      <DayByDay rows={rows} daily={data.daily} drillFor={drillFor} onDrill={setDrill} />

      <RecordsDialog spec={drill} onClose={() => setDrill(null)} />
    </Box>
  );
};

export default HistoryTab;
